import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";

var _class, _class2, _temp;

import React from 'react';
import { globalObject, isBrowser } from '@skbkontur/global-object';
import { isKeyArrowDown, isKeyArrowUp, isKeyEnter } from "../../../lib/events/keyboard/identifiers";
import { MenuSeparator } from "../../../components/MenuSeparator";
import { ThemeFactory } from "../../../lib/theming/ThemeFactory";
import { getDOMRect } from "../../../lib/dom/getDOMRect";
import { responsiveLayout } from "../../../components/ResponsiveLayout/decorator";
import { isNonNullable, isNullable } from "../../../lib/utils";
import { ScrollContainer } from "../../../components/ScrollContainer";
import { MenuItemDataTids } from "../../../components/MenuItem";
import { ThemeContext } from "../../../lib/theming/ThemeContext";
import { cx } from "../../../lib/theming/Emotion";
import { getRootNode, rootNode } from "../../../lib/rootNode";
import { addIconPaddingIfPartOfMenu } from "../../InternalMenu/addIconPaddingIfPartOfMenu";
import { isIE11 } from "../../../lib/client";
import { createPropsGetter } from "../../../lib/createPropsGetter";
import { isTheme2022 } from "../../../lib/theming/ThemeHelpers";
import { isIconPaddingEnabled } from "../../InternalMenu/isIconPaddingEnabled";
import { isInstanceOf } from "../../../lib/isInstanceOf";
import { getFullReactUIFlagsContext, ReactUIFeatureFlagsContext } from "../../../lib/featureFlagsContext";
import { styles } from "../Menu.styles";
import { isActiveElement } from "../isActiveElement";
import { MenuNavigation } from "../MenuNavigation";
import { MenuContext } from "../MenuContext";
export var MenuDataTids = {
  root: 'Menu__root'
};
export var Menu = responsiveLayout(_class = rootNode(_class = (_temp = _class2 = /*#__PURE__*/function (_React$PureComponent) {
  _inheritsLoose(Menu, _React$PureComponent);

  function Menu() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$PureComponent.call.apply(_React$PureComponent, [this].concat(args)) || this;
    _this.getProps = createPropsGetter(Menu.defaultProps);
    _this.state = {
      highlightedIndex: -1,
      maxHeight: _this.getProps().maxHeight || 'none',
      scrollState: 'top',
      enableIconPadding: false
    };
    _this.unmounted = false;
    _this.contentRef = /*#__PURE__*/React.createRef();
    _this.menuNavigation = new MenuNavigation(_this.contentRef, MenuItemDataTids.content);

    _this.focusOnRootElement = function () {
      var rootNode = getRootNode(_assertThisInitialized(_this));

      if (isInstanceOf(rootNode, globalObject.HTMLElement)) {
        rootNode == null ? void 0 : rootNode.focus();
      }
    };

    _this.renderHeader = function () {
      var _cx;

      return /*#__PURE__*/React.createElement("div", {
        className: cx((_cx = {}, _cx[styles.wrapper()] = true, _cx[styles.headerWrapper()] = true, _cx)),
        ref: function ref(el) {
          return _this.header = el;
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: styles.contentWrapper()
      }, _this.props.header), /*#__PURE__*/React.createElement("div", {
        className: styles.menuSeparatorWrapper(_this.theme)
      }, _this.state.scrollState !== 'top' && _this.renderMenuSeparatorWithNoMargin()));
    };

    _this.renderFooter = function () {
      var _cx2;

      return /*#__PURE__*/React.createElement("div", {
        className: cx((_cx2 = {}, _cx2[styles.wrapper()] = true, _cx2[styles.footerWrapper()] = true, _cx2)),
        ref: function ref(el) {
          return _this.footer = el;
        }
      }, /*#__PURE__*/React.createElement("div", {
        className: styles.menuSeparatorWrapper(_this.theme)
      }, _this.state.scrollState !== 'bottom' && _this.renderMenuSeparatorWithNoMargin()), /*#__PURE__*/React.createElement("div", {
        className: styles.contentWrapper()
      }, _this.props.footer));
    };

    _this.renderMenuSeparatorWithNoMargin = function () {
      return /*#__PURE__*/React.createElement(ThemeContext.Provider, {
        value: ThemeFactory.create({
          menuSeparatorMarginY: '0'
        }, _this.theme)
      }, /*#__PURE__*/React.createElement(MenuSeparator, null));
    };

    _this.getChildList = function () {
      if (_this.menuItemsAtAnyLevel) {
        return _this.props.children;
      }

      var enableIconPadding = isIconPaddingEnabled(_this.props.children, _this.props.preventIconsOffset);
      return React.Children.map(_this.props.children, function (child, index) {
        if (typeof child === 'string' || typeof child === 'number' || isNullable(child)) {
          return child;
        }

        var modifiedChild = addIconPaddingIfPartOfMenu(child, enableIconPadding);

        if (isActiveElement(modifiedChild)) {
          var highlight = _this.state.highlightedIndex === index;
          var ref = modifiedChild.ref;

          if (highlight && typeof modifiedChild.ref !== 'string') {
            ref = _this.refHighlighted.bind(_assertThisInitialized(_this), modifiedChild.ref);
          }

          return /*#__PURE__*/React.cloneElement(modifiedChild, {
            ref: ref,
            state: highlight ? 'hover' : modifiedChild.props.state,
            onClick: _this.select.bind(_assertThisInitialized(_this), index, false),
            onMouseEnter: _this.highlight.bind(_assertThisInitialized(_this), index),
            onMouseLeave: _this.unhighlight
          });
        }

        return modifiedChild;
      });
    };

    _this.setInitialSelection = function () {
      for (var i = _this.getProps().initialSelectedItemIndex; i > -1; i--) {
        _this.down();
      }
    };

    _this.shouldRecalculateMaxHeight = function (prevProps) {
      var _this$props = _this.props,
          header = _this$props.header,
          footer = _this$props.footer,
          children = _this$props.children;

      var maxHeight = _this.getProps().maxHeight;

      var prevMaxHeight = prevProps.maxHeight;
      var prevHeader = prevProps.header;
      var prevFooter = prevProps.footer;
      var prevChildrenCount = React.Children.count(prevProps.children);
      return maxHeight !== prevMaxHeight || footer !== prevFooter || header !== prevHeader || React.Children.count(children) !== prevChildrenCount;
    };

    _this.calculateMaxHeight = function () {
      var maxHeight = _this.getProps().maxHeight;

      var parsedMaxHeight = maxHeight;
      var rootNode = getRootNode(_assertThisInitialized(_this));

      if (typeof maxHeight === 'string' && isBrowser(globalObject) && rootNode) {
        var rootElementMaxHeight = globalObject.getComputedStyle == null ? void 0 : globalObject.getComputedStyle(rootNode).maxHeight;

        if (rootElementMaxHeight) {
          parsedMaxHeight = parseFloat(rootElementMaxHeight);
        }
      }

      var calculatedMaxHeight = typeof parsedMaxHeight === 'number' ? parsedMaxHeight + (_this.header && getDOMRect(_this.header).height || 0) + (_this.footer && getDOMRect(_this.footer).height || 0) : maxHeight;

      _this.setState({
        maxHeight: calculatedMaxHeight || 'none'
      });
    };

    _this.refScrollContainer = function (scrollContainer) {
      _this.scrollContainer = scrollContainer;
    };

    _this.scrollToSelected = function () {
      var highlightedItem = _this.menuItemsAtAnyLevel ? _this.menuNavigation.highlightedItem : _this.highlighted;

      if (_this.scrollContainer && highlightedItem) {
        var _rootNode = getRootNode(highlightedItem); // TODO: Remove this check once IF-647 is resolved


        if (isInstanceOf(_rootNode, globalObject.HTMLElement)) {
          _this.scrollContainer.scrollTo(_rootNode);
        }
      }
    };

    _this.scrollToTop = function () {
      if (_this.scrollContainer) {
        _this.scrollContainer.scrollToTop();
      }
    };

    _this.scrollToBottom = function () {
      if (_this.scrollContainer) {
        _this.scrollContainer.scrollToBottom();
      }
    };

    _this.scroll = function (nextIndex) {
      switch (nextIndex) {
        case 0:
          _this.scrollToTop == null ? void 0 : _this.scrollToTop();
          break;

        case _this.menuNavigation.items.length - 1:
          _this.scrollToBottom == null ? void 0 : _this.scrollToBottom();
          break;

        default:
          _this.scrollToSelected == null ? void 0 : _this.scrollToSelected();
      }
    };

    _this.highlight = function (index) {
      _this.setState({
        highlightedIndex: index
      });
    };

    _this.unhighlight = function () {
      _this.setState({
        highlightedIndex: -1
      });
    };

    _this.handleKeyDown = function (event) {
      if (typeof _this.props.onKeyDown === 'function') {
        _this.props.onKeyDown(event);
      }

      if (event.defaultPrevented) {
        return;
      }

      if (isKeyArrowUp(event)) {
        event.preventDefault();

        _this.up();
      } else if (isKeyArrowDown(event)) {
        event.preventDefault();

        _this.down();
      } else if (isKeyEnter(event)) {
        if (_this.menuItemsAtAnyLevel) {
          _this.menuNavigation.select(event);
        } else if (_this.highlighted && _this.highlighted.props.onClick) {
          _this.highlighted.props.onClick(event);
        }
      }
    };

    _this.getStyle = function (props) {
      if (props.align === 'right') {
        return {
          maxWidth: props.width,
          minWidth: props.width,
          maxHeight: _this.state.maxHeight
        };
      }

      return {
        width: props.width,
        maxHeight: _this.state.maxHeight
      };
    };

    _this.handleScrollStateChange = function (scrollState) {
      if (_this.state.scrollState !== scrollState) {
        _this.setState({
          scrollState: scrollState
        });
      }
    };

    _this.setEnableIconPadding = function (isIconPaddingEnabled) {
      !_this.getProps().preventIconsOffset && _this.setState({
        enableIconPadding: isIconPaddingEnabled
      });
    };

    return _this;
  }

  var _proto = Menu.prototype;

  _proto.componentWillUnmount = function componentWillUnmount() {
    this.unmounted = true;
  };

  _proto.componentDidMount = function componentDidMount() {
    this.setInitialSelection();
    this.calculateMaxHeight();
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    if (this.shouldRecalculateMaxHeight(prevProps)) {
      this.calculateMaxHeight();
    }

    if (prevProps.maxHeight !== this.getProps().maxHeight) {
      this.setState({
        maxHeight: this.props.maxHeight || 'none'
      });
    }
  };

  _proto.focus = function focus() {
    this.focusOnRootElement();
  };

  _proto.render = function render() {
    var _this2 = this;

    return /*#__PURE__*/React.createElement(ReactUIFeatureFlagsContext.Consumer, null, function (flags) {
      _this2.menuItemsAtAnyLevel = getFullReactUIFlagsContext(flags).menuItemsAtAnyLevel;
      return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, function (theme) {
        _this2.theme = theme;
        return _this2.renderMain();
      });
    });
  }
  /**
  * @public
  */
  ;

  _proto.up = function up() {
    this.move(-1);
  }
  /**
  * @public
  */
  ;

  _proto.down = function down() {
    this.move(1);
  }
  /**
  * @public
  */
  ;

  _proto.enter = function enter(event) {
    if (this.menuItemsAtAnyLevel) {
      var _this$menuNavigation$;

      (_this$menuNavigation$ = this.menuNavigation.highlightedItem) == null ? void 0 : _this$menuNavigation$.navigate();
      return this.menuNavigation.select(event);
    }

    return this.select(this.state.highlightedIndex, true, event);
  }
  /**
  * @public
  */
  ;

  _proto.reset = function reset() {
    if (this.menuItemsAtAnyLevel) {
      this.menuNavigation.reset();
    } else {
      this.setState({
        highlightedIndex: -1
      });
    }
  }
  /**
  * @public
  */
  ;

  _proto.hasHighlightedItem = function hasHighlightedItem() {
    if (this.menuItemsAtAnyLevel) {
      return !!this.menuNavigation.highlightedItem;
    }

    return this.state.highlightedIndex !== -1;
  };

  _proto.highlightItem = function highlightItem(index) {
    if (this.menuItemsAtAnyLevel) {
      this.menuNavigation.highlightByIndex(index);
    } else {
      this.highlight(index);
    }
  };

  _proto.renderMain = function renderMain() {
    var _cx3, _cx4;

    if (this.isEmpty()) {
      return null;
    }

    var _this$getProps = this.getProps(),
        hasShadow = _this$getProps.hasShadow,
        maxHeight = _this$getProps.maxHeight,
        preventWindowScroll = _this$getProps.preventWindowScroll;

    var offsetY = isTheme2022(this.theme) ? {
      top: "" + this.theme.scrollContainerScrollBarOffsetY,
      right: 0,
      bottom: "" + this.theme.scrollContainerScrollBarOffsetY
    } : {};
    var isMobile = this.isMobileLayout;
    return /*#__PURE__*/React.createElement("div", {
      "data-tid": MenuDataTids.root,
      className: cx(getAlignRightClass(this.props), (_cx3 = {}, _cx3[styles.root(this.theme)] = true, _cx3[styles.mobileRoot(this.theme)] = isMobile, _cx3[styles.shadow(this.theme)] = hasShadow && !isMobile, _cx3)),
      style: this.getStyle(this.props),
      id: this.props.id,
      onKeyDown: this.handleKeyDown,
      ref: this.setRootNode,
      tabIndex: 0
    }, this.props.header && this.renderHeader(), /*#__PURE__*/React.createElement(ScrollContainer, {
      ref: this.refScrollContainer,
      maxHeight: maxHeight,
      preventWindowScroll: preventWindowScroll,
      onScrollStateChange: this.handleScrollStateChange,
      disabled: this.props.disableScrollContainer,
      offsetY: offsetY
    }, /*#__PURE__*/React.createElement("div", {
      className: cx((_cx4 = {}, _cx4[styles.scrollContainer(this.theme)] = true, _cx4[styles.scrollContainerMobile(this.theme)] = isMobile, _cx4)),
      ref: this.contentRef
    }, /*#__PURE__*/React.createElement(MenuContext.Provider, {
      value: {
        navigation: this.menuNavigation,
        onItemClick: this.props.onItemClick,
        enableIconPadding: this.state.enableIconPadding,
        setEnableIconPadding: this.setEnableIconPadding
      }
    }, this.getChildList()))), this.props.footer && this.renderFooter());
  };

  _proto.refHighlighted = function refHighlighted(originalRef, menuItem) {
    this.highlighted = menuItem;

    if (typeof originalRef === 'function') {
      originalRef(menuItem);
    }
  };

  _proto.select = function select(index, shouldHandleHref, event) {
    var item = childrenToArray(this.props.children)[index];

    if (isActiveElement(item) && isBrowser(globalObject)) {
      if (shouldHandleHref && item.props.href) {
        if (item.props.target) {
          globalObject.open(item.props.href, item.props.target);
        } else {
          globalObject.location.href = item.props.href;
        }
      }

      if (item.props.onClick) {
        item.props.onClick(event);
      }

      if (this.props.onItemClick) {
        this.props.onItemClick(event);
      }

      return true;
    }

    return false;
  };

  _proto.move = function move(step) {
    var _this3 = this;

    if (this.unmounted) {
      // NOTE workaround, because `ComboBox` call `process.nextTick` in reducer
      return;
    }

    if (this.menuItemsAtAnyLevel) {
      var nextIndex = this.menuNavigation.move(step, this.getProps().cyclicSelection);
      this.scroll(nextIndex);
    } else {
      var _ret = function () {
        var children = childrenToArray(_this3.props.children);
        var activeElements = children.filter(isActiveElement);

        if (!activeElements.length) {
          return {
            v: void 0
          };
        }

        var index = _this3.state.highlightedIndex;

        var _loop = function _loop() {
          index += step;

          if (!_this3.getProps().cyclicSelection && (index < 0 || index > children.length)) {
            return {
              v: {
                v: void 0
              }
            };
          }

          if (index < 0) {
            index = children.length - 1;
          } else if (index > children.length) {
            index = 0;
          }

          var child = children[index];

          if (isActiveElement(child)) {
            _this3.setState({
              highlightedIndex: index
            }, function () {
              switch (activeElements.indexOf(child)) {
                case 0:
                  _this3.scrollToTop();

                  break;

                case activeElements.length - 1:
                  _this3.scrollToBottom();

                  break;

                default:
                  _this3.scrollToSelected();

              }
            });

            return {
              v: {
                v: void 0
              }
            };
          }
        };

        do {
          var _ret2 = _loop();

          if (typeof _ret2 === "object") return _ret2.v;
        } while (index !== _this3.state.highlightedIndex);
      }();

      if (typeof _ret === "object") return _ret.v;
    }
  };

  _proto.isEmpty = function isEmpty() {
    var children = this.props.children;
    return !children || !childrenToArray(children).filter(isNonNullable).length;
  };

  return Menu;
}(React.PureComponent), _class2.__KONTUR_REACT_UI__ = 'Menu', _class2.displayName = 'Menu', _class2.defaultProps = {
  align: 'left',
  width: 'auto',
  maxHeight: 300,
  hasShadow: true,
  preventWindowScroll: true,
  cyclicSelection: true,
  initialSelectedItemIndex: -1
}, _temp)) || _class) || _class;

function childrenToArray(children) {
  var ret = []; // Use forEach instead of map to avoid cloning for key unifying.

  React.Children.forEach(children, function (child) {
    ret.push(child);
  });
  return ret;
}

var getAlignRightClass = function getAlignRightClass(props) {
  if (props.align === 'right') {
    var _cx5;

    return cx((_cx5 = {}, _cx5[styles.alignRight()] = !isIE11, _cx5[styles.alignRightIE11()] = isIE11, _cx5[styles.alignRightIE11FixAutoWidth()] = isIE11 && props.width === 'auto', _cx5));
  }

  return null;
};